import * as React from 'react';
import { cn } from '../lib/utils';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

/** Types whose value is chosen from a browser picker rather than typed. */
const PICKER_TYPES = new Set(['date', 'datetime-local', 'month', 'time', 'week']);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', onClick, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      onClick={(event) => {
        onClick?.(event);
        // Chrome only opens a date/time picker when the small calendar glyph
        // itself is clicked — clicking the field does nothing, which reads as
        // "there is no picker". Open it for the whole control instead.
        // showPicker() throws without user activation and is absent in older
        // browsers, hence the guard.
        if (PICKER_TYPES.has(type) && !event.defaultPrevented) {
          const el = event.currentTarget as HTMLInputElement & { showPicker?: () => void };
          try {
            el.showPicker?.();
          } catch {
            // Not user-activated, or unsupported — the glyph still works.
          }
        }
      }}
      className={cn(
        'flex h-10 w-full rounded-md border border-line-strong bg-surface px-3 py-2',
        'font-sans text-base text-ink',
        'placeholder:text-ink-faint',
        'outline-none transition-colors',
        'focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/25',
        'disabled:opacity-60 disabled:bg-surface-sunk',
        'aria-invalid:border-danger aria-invalid:focus-visible:ring-danger/25',
        className
      )}
      {...props}
    />
  )
);
Input.displayName = 'Input';

export { Input };
